<script setup>
/* global axios */
// Quick Start — cria o contato (nome + telefone) e a conversa numa caixa, sem
// cadastrar o contato à mão, e abre a conversa criada para o agente mandar a
// mensagem por lá. Botão no header da lista de conversas.
import { ref, computed, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { frontendURL } from 'dashboard/helper/URLHelper.js';
import { LocalStorage } from 'shared/helpers/localStorage';
import NextButton from 'dashboard/components-next/button/Button.vue';

// Máscara brasileira: o agente digita só os dígitos e o campo mostra
// (67) 99163-8039, com o 55 fixo ao lado. O código do país entra SEMPRE no que é
// enviado — número gravado sem ele vira um contato que o WhatsApp não resolve, e a
// resposta do cliente cai fora da conversa aberta aqui. Ele fica FORA do campo de
// propósito: escrito dentro, o prefixo voltava como dígito digitado na tecla
// seguinte e se multiplicava a cada letra (55, 5555, 555555...).
const BR_COUNTRY_CODE = '55';
const DDD_LENGTH = 2;
const MAX_NATIONAL_LENGTH = 11;
const MIN_NATIONAL_LENGTH = 10;
const SUBSCRIBER_LENGTH = 8;
const MOBILE_PREFIX_LENGTH = 5;
const LANDLINE_PREFIX_LENGTH = 4;
// Caixa escolhida da última vez, por conta. A lista do seletor já vem limitada
// às caixas do agente (policy_scope), então lembrar a escolha nunca pré-seleciona
// uma caixa que ele não pode usar.
const LAST_INBOX_STORAGE_KEY = 'quick_start_last_inbox';

// O 55 da frente só é código do país quando sobra número demais para ser DDD +
// assinante (12 ou 13 dígitos). Sem essa condição o DDD 55, do Rio Grande do Sul,
// seria comido: 55999998888 viraria (99) 9999-8888.
const nationalDigits = value => {
  const digits = String(value ?? '').replace(/\D/g, '');
  const national =
    digits.length > MAX_NATIONAL_LENGTH && digits.startsWith(BR_COUNTRY_CODE)
      ? digits.slice(BR_COUNTRY_CODE.length)
      : digits;
  return national.slice(0, MAX_NATIONAL_LENGTH);
};

const formatNational = value => {
  const national = nationalDigits(value);
  if (!national) return '';

  const ddd = national.slice(0, DDD_LENGTH);
  const subscriber = national.slice(DDD_LENGTH);
  if (!subscriber) return `(${ddd}`;

  // Celular novo tem 9 dígitos e agrupa 5-4; fixo e celular antigo têm 8 e
  // agrupam 4-4. Enquanto o agente ainda está digitando (menos de 8), o 9 inicial
  // já indica celular, então o campo não reagrupa no meio do caminho.
  const isMobile =
    subscriber.length > SUBSCRIBER_LENGTH ||
    (subscriber.length < SUBSCRIBER_LENGTH && subscriber.startsWith('9'));
  const prefixLength = isMobile ? MOBILE_PREFIX_LENGTH : LANDLINE_PREFIX_LENGTH;
  const prefix = subscriber.slice(0, prefixLength);
  const suffix = subscriber.slice(prefixLength);
  const tail = suffix ? `${prefix}-${suffix}` : prefix;
  return `(${ddd}) ${tail}`;
};

const { t } = useI18n();
const router = useRouter();
const store = useStore();

const accountId = useMapGetter('getCurrentAccountId');
const inboxesGetter = useMapGetter('inboxes/getInboxes');
const inboxes = computed(() => inboxesGetter.value || []);
const lastInboxKey = computed(
  () => `${LAST_INBOX_STORAGE_KEY}::${accountId.value}`
);

const open = ref(false);
const name = ref('');
const phone = ref('');
const inboxId = ref('');
const saving = ref(false);
const phoneInput = ref(null);

const onPhoneInput = event => {
  const formatted = formatNational(event.target.value);
  phone.value = formatted;
  // O campo é controlado por :value, então quando o caractere digitado é
  // descartado pela máscara o valor não muda e o Vue não re-renderiza — sem
  // esta linha o caractere cru ficaria visível no input.
  event.target.value = formatted;
};

// Pré-seleciona a caixa usada por último e, quando não há nenhuma lembrada (ou
// ela não está mais disponível para este agente), a primeira da lista.
const preselectInbox = () => {
  if (inboxId.value || !inboxes.value.length) return;

  const remembered = LocalStorage.get(lastInboxKey.value);
  const match = inboxes.value.find(inbox => inbox.id === remembered);
  inboxId.value = match ? match.id : inboxes.value[0].id;
};

const toggle = async () => {
  open.value = !open.value;
  if (open.value) {
    if (!inboxes.value.length) await store.dispatch('inboxes/get');
    preselectInbox();
    await nextTick();
    phoneInput.value?.focus();
  }
};

const close = () => {
  open.value = false;
};

const submit = async () => {
  const national = nationalDigits(phone.value);
  if (national.length < MIN_NATIONAL_LENGTH) {
    useAlert(t('CONVERSATION.QUICK_START.INVALID_PHONE'));
    return;
  }
  const digits = `${BR_COUNTRY_CODE}${national}`;
  if (!inboxId.value) {
    useAlert(t('CONVERSATION.QUICK_START.SELECT_INBOX'));
    return;
  }

  saving.value = true;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${accountId.value}/kanban/quick_start`,
      {
        name: name.value.trim(),
        phone: digits,
        inbox_id: inboxId.value,
      }
    );
    LocalStorage.set(lastInboxKey.value, inboxId.value);
    open.value = false;
    name.value = '';
    phone.value = '';
    router.push(
      frontendURL(
        `accounts/${accountId.value}/conversations/${data.conversation_id}`
      )
    );
  } catch (e) {
    useAlert(e?.response?.data?.error || t('CONVERSATION.QUICK_START.ERROR'));
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <div v-on-clickaway="close" class="relative">
    <NextButton
      v-tooltip.bottom="t('CONVERSATION.QUICK_START.TOOLTIP')"
      icon="i-lucide-message-square-plus"
      slate
      xs
      faded
      @click="toggle"
    />
    <div
      v-if="open"
      class="absolute z-50 mt-2 w-72 p-3 rounded-xl shadow-lg border border-n-weak bg-n-solid-1 ltr:right-0 rtl:left-0 flex flex-col gap-2"
    >
      <div class="flex items-center justify-between">
        <span class="text-sm font-semibold text-n-slate-12">{{
          t('CONVERSATION.QUICK_START.TITLE')
        }}</span>
        <button class="text-n-slate-10 hover:text-n-slate-12" @click="close">
          <span class="i-lucide-x w-4 h-4 block" />
        </button>
      </div>
      <div class="relative">
        <span
          class="absolute text-sm -translate-y-1/2 pointer-events-none select-none text-n-slate-11 top-1/2 start-3"
        >
          {{ BR_COUNTRY_CODE }}
        </span>
        <input
          ref="phoneInput"
          :value="phone"
          type="tel"
          inputmode="numeric"
          :placeholder="t('CONVERSATION.QUICK_START.PHONE_PLACEHOLDER')"
          class="w-full py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 !ps-9 !pe-3 !mb-0"
          @input="onPhoneInput"
          @keydown.enter="submit"
        />
      </div>
      <input
        v-model="name"
        type="text"
        :placeholder="t('CONVERSATION.QUICK_START.NAME_PLACEHOLDER')"
        class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 !mb-0"
        @keydown.enter="submit"
      />
      <div class="relative">
        <select
          v-model="inboxId"
          class="w-full box-border appearance-none !bg-none !mb-0 !h-auto pl-3 pr-8 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        >
          <option value="">
            {{ t('CONVERSATION.QUICK_START.SELECT_INBOX') }}
          </option>
          <option v-for="i in inboxes" :key="i.id" :value="i.id">
            {{ i.name }}
          </option>
        </select>
        <span
          class="i-lucide-chevron-down pointer-events-none absolute ltr:right-2.5 rtl:left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-n-slate-10"
        />
      </div>
      <NextButton
        :label="
          saving
            ? t('CONVERSATION.QUICK_START.CREATING')
            : t('CONVERSATION.QUICK_START.CREATE_CONTACT')
        "
        blue
        sm
        :is-loading="saving"
        @click="submit"
      />
    </div>
  </div>
</template>
